import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AdminService } from 'src/app/services/admin-service';
import { EditorialIntelligenceService } from 'src/app/services/editorial-intelligence-service';

/**
 * Dev2 Editorial Article Review — a clean production-review workflow over the
 * frozen M1–M4 engine. Queue of generated articles on the left; the selected
 * article's generated view (markdown + SEO), evidence/reference panel and
 * media/reference panel on the right, with approve / reject / regenerate and an
 * editor-notes field. No publishing. Dev-only (route + tab are flag-gated).
 */
@Component({
  selector: 'pla-admin-editorial-review-queue',
  templateUrl: './admin-editorial-review-queue.component.html',
  styleUrls: ['./admin-editorial-review-queue.component.scss'],
})
export class AdminEditorialReviewQueueComponent implements OnInit {
  public verified = false;
  public loadingQueue = true;

  public articles: any[] = [];
  public statusFilter: 'all' | 'in_review' | 'approved' | 'rejected' | 'changes_requested' = 'all';
  public readonly filters = [
    { key: 'all', label: 'All' },
    { key: 'in_review', label: 'In review' },
    { key: 'changes_requested', label: 'Changes requested' },
    { key: 'approved', label: 'Approved' },
    { key: 'rejected', label: 'Rejected' },
  ];

  public selected: any = null;   // queue row {id, registration, status, headline, confidence}
  public loadingDetail = false;
  public article: any = null;    // generate-article response
  public dossier: any = null;    // getDossier().dossier (evidence + media)
  public regenerating = false;
  public acting = false;
  public notesDraft = '';
  public activePanel: 'article' | 'evidence' | 'media' = 'article';
  public mediaPending = new Set<string>();

  public toastMessage = '';
  private toastTimer: any = null;

  constructor(
    private router: Router,
    private adminService: AdminService,
    private intel: EditorialIntelligenceService
  ) {}

  ngOnInit(): void {
    this.adminService.verify().subscribe(
      (data: { admin: boolean }) => {
        if (!data || !data.admin) {
          this.router.navigate(['/landing']);
          return;
        }
        this.verified = true;
        this.loadQueue();
      },
      () => this.router.navigate(['/landing'])
    );
  }

  // ---- Queue ------------------------------------------------------------------

  public loadQueue(): void {
    this.loadingQueue = true;
    this.intel.listArticles(this.statusFilter).subscribe((rows) => {
      this.articles = (rows || []).sort((a, b) =>
        String(b.updated_at || '').localeCompare(String(a.updated_at || '')));
      this.loadingQueue = false;
    });
  }

  public setFilter(key: any): void {
    this.statusFilter = key;
    this.loadQueue();
  }

  // ---- Selection + detail load ------------------------------------------------

  public select(row: any): void {
    this.selected = row;
    this.article = null;
    this.dossier = null;
    this.activePanel = 'article';
    this.notesDraft = row.reviewer_notes || '';
    this.loadingDetail = true;
    this.intel.generateArticle(row.registration).subscribe((a) => (this.article = a));
    this.intel.getDossier(row.registration).subscribe((res) => {
      this.dossier = res ? res.dossier : null;
      this.loadingDetail = false;
    });
  }

  public regenerate(): void {
    if (!this.selected || this.regenerating) return;
    this.regenerating = true;
    this.toast('Regenerating article…');
    this.intel.generateArticle(this.selected.registration).subscribe(
      (a) => {
        this.regenerating = false;
        this.article = a;
        this.toast(a?.status === 'generated' ? 'Article regenerated.' : 'Not ready for article.');
      },
      () => {
        this.regenerating = false;
        this.toast('Regeneration failed.');
      }
    );
  }

  // ---- Review actions ---------------------------------------------------------

  private act(action: 'approve' | 'reject' | 'request-changes', done: string): void {
    if (!this.selected || this.acting) return;
    this.acting = true;
    this.intel.articleAction(this.selected.id, action, this.notesDraft).subscribe(
      (res) => {
        this.acting = false;
        if (!res) {
          this.toast('Action failed — no changes were made.');
          return;
        }
        const status = res.article ? res.article.status : res.status;
        if (status) this.selected.status = status;
        this.selected.reviewer_notes = this.notesDraft;
        this.toast(done);
        this.loadQueue();
      },
      () => {
        this.acting = false;
        this.toast('Action failed — no changes were made.');
      }
    );
  }

  public approve(): void {
    this.act('approve', 'Approved.');
  }
  public reject(): void {
    this.act('reject', 'Rejected.');
  }
  public requestChanges(): void {
    this.act('request-changes', 'Sent back for changes.');
  }

  public saveNotes(): void {
    if (!this.selected) return;
    this.intel.saveArticleNotes(this.selected.id, this.notesDraft).subscribe((res) => {
      if (res) {
        this.selected.reviewer_notes = this.notesDraft;
        this.toast('Notes saved.');
      } else {
        this.toast('Could not save notes.');
      }
    });
  }

  // ---- Media panel actions ----------------------------------------------------

  public mediaActionRun(m: any, action: 'approve' | 'reject' | 'flag'): void {
    if (m.id == null) return;
    const key = String(m.id);
    this.mediaPending.add(key);
    this.intel.mediaAction(m.id, action).subscribe((ok) => {
      this.mediaPending.delete(key);
      if (!ok) {
        this.toast('Media action failed.');
        return;
      }
      if (action === 'approve') {
        m.status = 'approved';
        m.approvedForDisplay = true;
      } else if (action === 'reject') {
        m.status = 'rejected';
      } else {
        m.status = 'flagged';
      }
      this.toast('Saved.');
    });
  }

  public isMediaPending(m: any): boolean {
    return this.mediaPending.has(String(m.id));
  }

  // ---- Thumbnails (remote references only; never downloaded/rehosted) ----------
  public brokenThumbs = new Set<string>();

  public thumbFor(m: any): string | null {
    if (m.thumbnailUrl) return m.thumbnailUrl;
    const u: string = m.url || m.embedUrl || '';
    const match = /(?:v=|youtu\.be\/|\/embed\/)([A-Za-z0-9_-]{11})/.exec(u);
    return match ? `https://i.ytimg.com/vi/${match[1]}/hqdefault.jpg` : null;
  }

  public onThumbError(m: any): void {
    this.brokenThumbs.add(String(m.id ?? m.url));
  }

  public thumbBroken(m: any): boolean {
    return this.brokenThumbs.has(String(m.id ?? m.url));
  }

  public get activeMedia(): any[] {
    return (this.dossier?.media || []).filter(
      (m: any) => m.status !== 'retired' && m.status !== 'rejected'
    );
  }

  /** Manually-imported references (shown in the evidence/reference panel too). */
  public get manualReferences(): any[] {
    return this.activeMedia.filter((m: any) => m.provider === 'manual_import');
  }

  /** Re-fetch the dossier for the selected article (after a manual import). */
  public reloadDossier(): void {
    if (!this.selected) return;
    this.intel.getDossier(this.selected.registration).subscribe((res) => {
      this.dossier = res ? res.dossier : this.dossier;
    });
    this.toast('Imported references added.');
  }

  // ---- Presentation helpers ---------------------------------------------------

  public statusClass(status?: string): string {
    switch (status) {
      case 'approved':
        return 'ok';
      case 'rejected':
        return 'bad';
      case 'in_review':
      case 'changes_requested':
        return 'warn';
      default:
        return 'muted';
    }
  }

  public statusLabel(status?: string): string {
    return (status || 'in_review').replace(/_/g, ' ');
  }

  /** Map the dossier's 7 rights codes to the 5 publication buckets shown to editors. */
  public rights5(status?: string): string {
    switch (status) {
      case 'platex_owned':
      case 'public_domain':
      case 'owned':
      case 'licensed':
        return 'publish-ready';
      case 'embed_only':
      case 'embed_allowed':
        return 'embed-only';
      case 'licence_required':
        return 'licence required';
      case 'permission_required':
        return 'permission required';
      default:
        return 'reference';
    }
  }

  public rightsClass(status?: string): string {
    const b = this.rights5(status);
    if (b === 'publish-ready') return 'ok';
    if (b === 'embed-only') return 'info';
    if (b === 'reference') return 'muted';
    return 'warn';
  }

  public toast(message: string): void {
    this.toastMessage = message;
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => (this.toastMessage = ''), 3000);
  }
}
